import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { cn } from '@unitfield/ui/lib/utils';
import { ApplicantRequestError, applicantRequest, applicantStatusQuery, applicantStatusPath, type ApplicantStatus } from './continuation-query';
import { applicantJourney, destinationLabel, journeyEnded, type Journey, type JourneyStop } from './continuation-journey';
import { ContinuationScreening } from './continuation-screening';
import { PublicBrandProvider, PublicFooter, PublicSpinner, PublicTopBar } from '../shell';
import type { PublicBranding } from '../shell/public-branding';
import shell from '../shell/shell.module.css';
import styles from './continuation-page.module.css';

const subscribe = () => () => {};
type SigningSession = { clientId: string; signUrl: string; expiresAtUtc: string };

/** The business's name from branding. The slug is never turned into a name (spec §2). */
export function organizationDisplayName(_organizationSlug: string, businessName?: string | null): string {
  return businessName?.trim() || '';
}
/** Readable ink for a tile filled with the business colour: white or near-black, whichever contrasts more. */
export function monogramColors(primaryColor?: string | null): { background: string; color: string } | undefined {
  const hex = primaryColor?.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)?.[1];
  if (!hex) return undefined;
  const full = hex.length === 3 ? hex.replace(/./g, digit => digit + digit) : hex;
  const channel = (offset: number) => { const value = parseInt(full.slice(offset, offset + 2), 16) / 255; return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4; };
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
  const ink = 0.0091; // relative luminance of #1a1a1a
  const onWhite = 1.05 / (luminance + 0.05), onInk = (luminance + 0.05) / (ink + 0.05);
  return { background: `#${full}`, color: onWhite >= onInk ? '#ffffff' : '#1a1a1a' };
}

const residentLabels: Record<NonNullable<ApplicantStatus['continuation']>['residentState'], string> = { NotReady: 'Not ready', InvitationReady: 'Ready to continue', Unavailable: 'Access unavailable', Linked: 'Ready' };

/** A question-sized heading with one sentence, the code screen's and the next stop's. */
function TaskHeading({ title, children, level = 1 }: { title: string; children?: ReactNode; level?: 1 | 2 }) {
  const Heading = level === 1 ? 'h1' : 'h2';
  return <div className={styles.heading} data-level={level}>
    <Heading>{title}</Heading>
    {children ? <p>{children}</p> : null}
  </div>;
}

function StopMark({ stop, index }: { stop: JourneyStop; index: number }) {
  return <span className={styles.stopMark} aria-hidden="true">
    {stop.state === 'done' ? <UnitfieldIcon name="check" size={16} strokeWidth={2.4} />
      : stop.key === 'move' ? <UnitfieldIcon name="key" size={16} />
        : <span className="num">{index + 1}</span>}
  </span>;
}

/**
 * The track: a 6px meter through up to five stops. The next stop's work sits under the track on
 * wide screens and under its own stop on a phone; it is rendered once and placed by CSS order.
 */
function JourneyTrack({ journey, children }: { journey: Journey; children: ReactNode }) {
  const currentIndex = journey.stops.findIndex(stop => stop.state === 'current');
  return <div className={styles.track} style={{ '--stops': journey.stops.length, '--panel-order': currentIndex === -1 ? journey.stops.length : currentIndex } as CSSProperties}>
    <ol className={styles.stops} aria-label="Application progress">
      {journey.stops.map((stop, index) => <li key={stop.key} data-state={stop.state} className={styles.stop} style={{ '--stop-index': index } as CSSProperties}>
        <StopMark stop={stop} index={index} />
        <span className={styles.stopText}>
          <span className={styles.stopLabel}>{stop.label}<span className="sr-only">{stop.state === 'done' ? ', done' : stop.state === 'current' ? ', current step' : ', upcoming'}</span></span>
          {stop.detail ? <span className={cn(styles.stopDetail, 'num')} data-your-turn={stop.yourTurn && stop.state === 'current' ? '' : undefined}>{stop.detail}</span> : null}
        </span>
      </li>)}
    </ol>
    <div className={styles.now}>{children}</div>
  </div>;
}

/** The home at the end of the path, or a key tile when the status has no photo. */
function Destination({ status, title, today }: { status: ApplicantStatus; title: string | null; today: Date }) {
  const [failed, setFailed] = useState<string | null>(null);
  const label = destinationLabel(status, today);
  const photo = status.coverImageUrl && failed !== status.coverImageUrl ? status.coverImageUrl : null;
  return <figure className={styles.destination} data-photo={photo ? '' : undefined}>
    {photo ? <img src={photo} alt="" onError={() => setFailed(photo)} /> : <span className={styles.keyTile} aria-hidden="true"><UnitfieldIcon name="key" size={26} /></span>}
    <figcaption className={styles.destinationText}>
      {label ? <span className={cn(styles.destinationChip, 'num')}><UnitfieldIcon name="key" size={15} aria-hidden="true" />{label}</span> : null}
      {title ? <span className={styles.destinationTitle}>{title}</span> : null}
    </figcaption>
  </figure>;
}

/**
 * The status of one submitted application, for the signed-in customer who made it (spec 4.5). The customer session is the
 * only capability: no handle, no recovery code. `branding` is the business's public brand; `accountHref` opens the branded portal.
 */
export function ApplicantContinuationView({ organizationSlug, applicationId, branding: publicBranding, accountHref = '/account', onSessionEnded, live = true, now: pinnedNow }: { organizationSlug: string; applicationId: string; branding: PublicBranding; accountHref?: string; /** The customer's session ended: the page sends them back through sign-in. */ onSessionEnded?: () => void; /** False for the Brand settings preview and stories: they show seeded data and must never poll or refetch from a host that has no BFF. */ live?: boolean; /** Stories pin the day. */ now?: Date }) {
  const hydrated = useSyncExternalStore(subscribe, () => true, () => false), pending = useRef(false);
  const statusQuery = applicantStatusQuery(organizationSlug, applicationId);
  const query = useQuery(live ? statusQuery : { ...statusQuery, enabled: false, refetchInterval: false, refetchOnWindowFocus: false });
  const [busy, setBusy] = useState(false), [message, setMessage] = useState<string | null>(null);
  const sessionEnded = query.isError && query.error instanceof ApplicantRequestError && query.error.status === 401;
  useEffect(() => { if (sessionEnded) onSessionEnded?.(); }, [sessionEnded, onSessionEnded]);
  const status = query.data;
  /** The signing provider's hosted page. This page never frames it: our content security policy allows no frames. */
  async function signAgreement() {
    if (!hydrated || pending.current) return; pending.current = true; setBusy(true); setMessage(null);
    try {
      const session = await applicantRequest<SigningSession>(`${applicantStatusPath(applicationId)}/signing-session`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const destination = new URL(session.signUrl);
      if (destination.protocol !== 'https:' || destination.username || destination.password) throw new Error('Agreement signing could not be confirmed. Try again.');
      window.location.assign(destination.href);
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Agreement signing is temporarily unavailable.'); setBusy(false); pending.current = false; }
  }

  const today = useMemo(() => pinnedNow ?? new Date(), [pinnedNow]);
  const org = organizationDisplayName(organizationSlug, publicBranding.name || status?.continuation?.businessName);
  const business = org || 'the rental business';
  const continuation = status?.continuation ?? null;
  const verified = Boolean(status);
  const unit = verified ? status?.unitName?.trim() || null : null;
  const space = verified ? [status?.unitName, status?.propertyName].map(part => part?.trim()).filter(Boolean).join(' · ') || continuation?.spaceName || null : null;
  const refresh = <button type="button" className={styles.quietAction} disabled={!hydrated || query.isFetching} onClick={() => void query.refetch()}>
    <UnitfieldIcon name="refresh" size={15} aria-hidden="true" />{query.isFetching ? 'Checking…' : 'Refresh status'}
  </button>;
  const alert = message ? <p role="alert" className={styles.alert}>{message}</p> : null;

  // `title`/`lead` head the page; `next` is the current stop's work; `ended` replaces the journey.
  let stepKey: string; let title: string; let lead: ReactNode = null; let next: ReactNode = null; let ended: ReactNode = null;
  let body: ReactNode = null;
  if (query.isError) {
    stepKey = 'error'; title = 'We couldn’t load your application'; lead = 'This is usually temporary. Try again in a moment.';
    body = <>
      <p role="alert" className="sr-only">Application status is temporarily unavailable.</p>
      <button type="button" className={cn(shell.btn, shell.btnBrand)} disabled={!hydrated || query.isFetching} onClick={() => void query.refetch()}>Check status again</button>
    </>;
  } else if (!status) {
    stepKey = 'loading'; title = 'Application status';
    body = <div className={styles.loading}><PublicSpinner size={32} decorative /><p role="status">Checking application status…</p></div>;
  } else if (status.stage === 'Deleted') {
    stepKey = 'removed'; title = 'This application is no longer available';
    ended = <p className={styles.endedText}>{org ? `${org} closed or removed it.` : 'The rental business closed or removed it.'} Contact them if you think this is a mistake.</p>;
  } else if (status.stage === 'Declined') {
    stepKey = 'declined'; title = 'Application not approved';
    ended = <p className={styles.endedText}>{org ? `${org} has` : 'The rental business has'} made a decision on this application. Contact them if you have questions about it.</p>;
  } else {
    title = status.stage === 'Approved' ? 'Your application was approved' : 'Application sent';
    lead = 'We’ll email you when anything changes.';
    if (continuation) {
      stepKey = `agreement-${continuation.nextAction}`;
      const copy = ({
        SignAgreement: { title: 'Next: sign your lease', body: `Review and sign your agreement. Access opens after ${business} confirms completion.` },
        CheckSigningEmail: { title: 'Next: sign from your email', body: `${business.charAt(0).toUpperCase()}${business.slice(1)} sent your agreement for signature. Look for the signing invitation in your email.` },
        OpenResident: { title: 'Next: set up your resident access', body: `${business.charAt(0).toUpperCase()}${business.slice(1)} will email you an invitation to Unitfield Resident. Open it to see your home. Resident access: ${residentLabels[continuation.residentState].toLowerCase()}.` },
        OpenAccount: { title: 'Next: open your resident account', body: `Your account at ${business} is ready. Open it to see your home, pay rent and send requests.` },
        Wait: { title: 'Next: your lease', body: `${business.charAt(0).toUpperCase()}${business.slice(1)} will confirm your next step when it’s ready.` },
      } as const)[continuation.nextAction];
      next = <>
        <div className={styles.nowRow}>
          <TaskHeading title={copy.title} level={2}>{copy.body}</TaskHeading>
          {continuation.nextAction === 'SignAgreement' ? <button type="button" className={cn(shell.btn, shell.btnBrand)} disabled={!hydrated || busy} onClick={() => void signAgreement()}>{busy ? 'Please wait…' : 'Review and sign agreement'}</button>
            : continuation.nextAction === 'OpenAccount' ? <a className={cn(shell.btn, shell.btnBrand)} href={accountHref}>Open your account</a> : null}
        </div>
      </>;
    } else if (status.screening && status.screening.state !== 'completed') {
      stepKey = `screening-${status.screening.state}`;
      next = <ContinuationScreening compact screening={status.screening} businessName={business} hydrated={hydrated} refreshing={query.isFetching} onRefresh={() => void query.refetch()} />;
    } else if (status.stage === 'Approved') {
      stepKey = 'approved';
      next = <TaskHeading title="Next: your lease" level={2}>
        {status.hasLease ? `A lease has been created${unit ? ` for ${unit}` : ''}. Follow ${org ? `${org}’s` : 'the rental business’s'} instructions.` : `${business.charAt(0).toUpperCase()}${business.slice(1)} will confirm your next step${unit ? ` for ${unit}` : ''} when it’s ready.`}
      </TaskHeading>;
    } else if (status.screening) {
      stepKey = 'screening-completed';
      next = <ContinuationScreening compact screening={status.screening} businessName={business} hydrated={hydrated} refreshing={query.isFetching} onRefresh={() => void query.refetch()} />;
    } else {
      stepKey = 'review';
      next = <TaskHeading title={org ? `Next: ${org}’s decision` : 'Next: the decision'} level={2}>
        Your application{unit ? ` for ${unit}` : ''} is with {business}. Nothing else is needed from you right now. The decision comes from them, not from Unitfield.
      </TaskHeading>;
    }
  }

  const journey = status && verified && !journeyEnded(status) ? applicantJourney(status, org, today) : null;
  const extras = <>
    {alert}
    {status && !query.isError && status.screening?.state !== 'action_required' ? refresh : null}
  </>;

  return <PublicBrandProvider branding={publicBranding} className={styles.viewport}>
    <section className={cn(shell.page, styles.page)} aria-label={org ? `${org} rental application` : 'Rental application'} data-step={stepKey}>
      <PublicTopBar />
      {journey && status ? <>
        <div className={styles.head}>
          <h1 className={styles.title}>{title}</h1>
          {lead ? <p className={styles.lead}>{lead}</p> : null}
        </div>
        <div className={styles.journey}>
          <div className={styles.journeyMain}>
            <JourneyTrack journey={journey}>
              {next}
              <div className={styles.extras}>{extras}</div>
            </JourneyTrack>
          </div>
          <Destination status={status} title={space} today={today} />
        </div>
      </> : <div className={styles.task} key={stepKey}>
        <div className={styles.head}>
          <h1 className={styles.title} data-size="task">{title}</h1>
          {lead ? <p className={styles.lead}>{lead}</p> : null}
        </div>
        {ended}
        {body}
        <div className={styles.extras}>{extras}</div>
      </div>}
      <PublicFooter />
    </section>
  </PublicBrandProvider>;
}
